import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';

import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import { useGetMinimumGuarantee } from 'services/hooks/minimumGuarantee';
import type { MinimumGuaranteeResponseDto } from 'services/hooks/minimumGuarantee';
import { translation } from 'utils/translation';

import Card from 'components/card/Card';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import Flex from 'components/util/Flex';

import { formatPayoutDate, formatSettlementPeriod } from './utils/formatSettlement';
import AmountValue from '../shared/AmountValue';
import { SInformationItemWrapper } from '../shared/style';

/**
 * 별지기 MG 지급 근거 섹션 (#2182).
 *
 * 자기 위치를 모르는 독립 단위다. 대시보드 안의 블록으로 꽂든 전용 라우트의 본문으로 쓰든
 * 이 파일은 바뀌지 않는다. 노출 여부는 부모가 `StargazerGate` 로 결정한다.
 */
function MinimumGuaranteeContainer() {
  const { t } = useTranslation();
  const { data, isError } = useGetMinimumGuarantee();

  // 조회가 실패하면 섹션을 통째로 감춘다. 정책 C 의 "말없이 감춘다" 와 같은 철학이고
  // `StargazerGate` 도 그렇게 동작한다. 제목만 남은 빈 카드를 보여줄 이유가 없다.
  if (isError) return null;

  return (
    <Card isExpanded>
      <Card.Header>
        <Card.Title>{t('chat.rewards_mg_title')}</Card.Title>
      </Card.Header>
      <Card.Body>
        <MinimumGuaranteeBody data={data} />
      </Card.Body>
    </Card>
  );
}

export default withErrorBoundary(MinimumGuaranteeContainer);

type MinimumGuaranteeBodyProps = {
  data?: MinimumGuaranteeResponseDto;
};

function MinimumGuaranteeBody({ data }: MinimumGuaranteeBodyProps) {
  const { t } = useTranslation();
  const { isBankAccountVerificationCompleted, isConvertibleToBubble } = useCheckVerificationsComplete();

  // 금액 마스킹 조건은 수익 정보 카드와 같다. 인증이 끝나지 않은 유저에게는 금액을 가린다.
  const isBlur = !(isBankAccountVerificationCompleted || isConvertibleToBubble);

  // BE 계약이 초안이라 타입이 non-null 이어도 실제로는 빠져 올 수 있다. 여기서 접근이 터지면
  // 섹션이 아니라 대시보드 전체가 에러 패널로 덮이므로 한 단계씩 방어한다.
  const settlement = data?.settlement;
  const settlementPeriod =
    settlement?.startAt && settlement?.endAt ? formatSettlementPeriod(settlement.startAt, settlement.endAt) : null;
  const payoutDate = settlement?.payoutDate ? formatPayoutDate(settlement.payoutDate) : null;

  // 전월 확정분은 확정이 끝난 동안(대략 1일~지급일)만 함께 보여준다.
  const finalizedPreviousMonth = data?.previousMonth?.isFinalized ? data.previousMonth : null;

  // 전월분 지급일은 최상위 settlement 이 아니라 previousMonth 자기 것을 쓴다.
  // 최상위는 당월분이라 다음 달 10일을 가리키는데, 1~10일에 실제로 나가는 돈은 전월분(이번 달 10일)이다.
  const previousMonthPayoutDate = finalizedPreviousMonth?.settlement?.payoutDate
    ? formatPayoutDate(finalizedPreviousMonth.settlement.payoutDate)
    : null;

  return (
    <Flex direction="column">
      <SInformationItemWrapper>
        <span>
          {t('chat.rewards_mg_current_month_revenue_pre_tax')}
          {translation('chat.rewards_currency_krw')}
        </span>
        <AmountValue amountFormat={data?.currentMonth?.preTaxAmountFormat} isBlur={isBlur} />
      </SInformationItemWrapper>

      <SInformationItemWrapper>
        <span>
          {t('chat.rewards_mg_current_month_revenue_after_tax')}
          {translation('chat.rewards_currency_krw')}
        </span>
        <AmountValue amountFormat={data?.currentMonth?.afterTaxAmountFormat} isBlur={isBlur} />
      </SInformationItemWrapper>

      {settlementPeriod && (
        <SInformationItemWrapper>
          <span>{t('chat.rewards_mg_settlement_period')}</span>
          <span className="bold">{settlementPeriod}</span>
        </SInformationItemWrapper>
      )}

      {payoutDate && (
        <SInformationItemWrapper>
          <span>{t('chat.rewards_mg_payout_date')}</span>
          <span className="bold">{payoutDate}</span>
        </SInformationItemWrapper>
      )}

      {finalizedPreviousMonth && (
        <>
          {/* 당월과 같은 형태로 세전·세후를 각각 표기한다 (기획 확인 3번). 기준이 없으면 CS 가 들어온다 */}
          <SInformationItemWrapper>
            <span>
              {t('chat.rewards_mg_previous_month_revenue_pre_tax')}
              {translation('chat.rewards_currency_krw')}
            </span>
            <AmountValue amountFormat={finalizedPreviousMonth.preTaxAmountFormat} isBlur={isBlur} />
          </SInformationItemWrapper>
          <SInformationItemWrapper>
            <span>
              {t('chat.rewards_mg_previous_month_revenue_after_tax')}
              {translation('chat.rewards_currency_krw')}
            </span>
            <AmountValue amountFormat={finalizedPreviousMonth.afterTaxAmountFormat} isBlur={isBlur} />
          </SInformationItemWrapper>
          {previousMonthPayoutDate && (
            <SInformationItemWrapper>
              <span>{t('chat.rewards_mg_previous_month_payout_date')}</span>
              <span className="bold">{previousMonthPayoutDate}</span>
            </SInformationItemWrapper>
          )}
        </>
      )}

      {/*
        금액 노출은 BE 가 응답 단계에서 정한다 — 1차에는 `minimumGuarantee` 를 항상 null 로 비워
        기획 확인 2번("숫자 노출은 후속")을 지킨다(BE 확정 코멘트, MR !1195). FE 플래그로 한 번 더
        막던 것을 걷어냈다. 같은 정책을 두 곳에서 지키면 BE 가 값을 채웠는데 화면에 안 나오는
        함정이 생기고, 공개 시 FE 배포가 한 번 더 필요해진다.
      */}
      {data?.minimumGuarantee && (
        <>
          <SInformationItemWrapper>
            <span>
              {t('chat.rewards_mg_contract_amount')}
              {translation('chat.rewards_currency_krw')}
            </span>
            <AmountValue amountFormat={data.minimumGuarantee.amountFormat} isBlur={isBlur} />
          </SInformationItemWrapper>
          <SInformationItemWrapper>
            <span>
              {t('chat.rewards_mg_shortfall')}
              {translation('chat.rewards_currency_krw')}
            </span>
            <AmountValue amountFormat={data.minimumGuarantee.shortfallFormat} isBlur={isBlur} />
          </SInformationItemWrapper>
        </>
      )}

      {/* 정책 B-1: 접지 않고 상시 노출. 툴팁으로 감추지 않는다 */}
      <SNotice>{t('chat.rewards_mg_notice')}</SNotice>
    </Flex>
  );
}

const SNotice = styled.p`
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.bg.secondary};
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.caption1_m};
  white-space: pre-line;

  @media ${({ theme }) => theme.device.large} {
    margin-top: 12px;
    padding: 12px;
  }
`;
